import { AggregationType } from "@dto/fields";
import { AND, FieldKey, Filter, IN, INF_EQUAL, SUP_EQUAL } from "@dto/records";
import { checkFormula, getFieldKey } from "@utils/model";
import { TW_AdvancedPeriodEditorContainer, TW_AdvancedPeriodEditorShortcutButton, TW_AdvancedPeriodEditorShortcutButtonsContainer, TW_AdvancedPeriodEditorSectionContainer, TW_AdvancedPeriodEditorSectionHeader, TW_AdvancedPeriodEditorSectionIcon, TW_AdvancedPeriodEditorSectionLabel, TW_DateFilterValueEditor, TW_DateFilterValueEditor_editing, TW_DateFilterValueEditor_FunctionButton, TW_DateFilterValueEditor_PeriodFormulaButton, TW_DateFilterValueEditor_Wrapper, TW_FilterEditorValue_Wrapper, TW_PeriodFilterValueEditor_From, TW_PeriodFilterValueEditor_To, TW_AdvancedPeriodEditorPeriodFormulaContainer, TW_AdvancedPeriodEditorPeriodFormulaButtonValidate, useHover, getWsButtonStyle, useIconHover, TW_AdvancedPeriodEditorTrashIcon, TW_AdvancedPeriodEditorDateFormulasContainer, TW_AdvancedPeriodEditorDateFormulaItem, TW_AdvancedPeriodEditorFormulaTogglePressed, TW_AdvancedPeriodEditorFormulaToggle, TW_DateFilterValueEditor_DateFormulaButton } from "@utils/twClasses";
import { displayDate } from "@utils/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore, useUserStore } from "src/store";
import { ReactComponent as FunctionSVG } from "@img/function.svg";
import { ReactComponent as FunctionStruckSVG } from "@img/function_struck.svg";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Button } from "@comps/ui/button";
import { TFunction } from "i18next";
import { Label } from "@comps/ui/label";
import { ChevronDown, ChevronRight } from "lucide-react";
import { FormulaEditor } from "@app/objects/FormulaEditor";
import { GRAY_300_COLOR, GRAY_600_COLOR, GRAY_800_COLOR } from "@utils/colors";
import { translate } from "src/i18n/i18n";
import { Tooltip, TooltipTrigger, TooltipProvider, TooltipContent } from "@comps/ui/tooltip";

type DateFilterValueEditorProps = {
    selectedPathKey: string | undefined;
    filter: Filter;
    validateFilter: (filter: Filter) => void;
    disabled: boolean
}

const getShortcuts = (t: TFunction) => [
    { label: t("field.formula.variable.label.today"), formula: "${TODAY}" },
    { label: t("field.formula.variable.label.this_week"), formula: "${THIS_WEEK}" },
    { label: t("field.formula.variable.label.this_month"), formula: "${THIS_MONTH}" },
    { label: t("field.formula.variable.label.this_quarter"), formula: "${THIS_QUARTER}" },
    { label: t("field.formula.variable.label.this_year"), formula: "${THIS_YEAR}" },
]

const AUTHORIZED_KEYS = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "-", "Home", "End"];

export function DateFilterValueEditor({ selectedPathKey, filter, validateFilter, disabled }: DateFilterValueEditorProps) {

    const lang = useUserStore(state => state.lang);
    const [periodFormula, setPeriodFormula] = useState<string | null>(null);
    const [startDateFormula, setStartDateFormula] = useState<string | null>(null);
    const [endDateFormula, setEndDateFormula] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [editStartDate, setEditStartDate] = useState(false);
    const [editEndDate, setEditEndDate] = useState(false);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

    const { t } = useTranslation();
    const shortcuts = getShortcuts(t);

    const fieldByKeys = useModelStore((state) => state.fieldByKeys);
    const fieldKey = selectedPathKey ? getFieldKey(selectedPathKey) : undefined;
    const field = fieldKey ? (fieldByKeys?.get(fieldKey.fieldKey) || null) : null;

    useEffect(() => {
        if (filter) {
            const fieldFilter = filter.groupFilter;
            const filters = fieldFilter?.filters;
            if (filters) {
                filters.forEach(f => {
                    if (f.singleFilter) {
                        const sFilter = f.singleFilter;
                        if (sFilter.operator == SUP_EQUAL && sFilter.value) {
                            setStartDate(sFilter.value);
                        }
                        if (sFilter.operator == INF_EQUAL && sFilter.value) {
                            setEndDate(sFilter.value);
                        }
                        if (sFilter.formula && sFilter.operator == IN) {
                            setPeriodFormula(sFilter.formula);
                        }
                        if (sFilter.formula && sFilter.operator == SUP_EQUAL) {
                            setStartDateFormula(sFilter.formula);
                        }
                        if (sFilter.formula && sFilter.operator == INF_EQUAL) {
                            setEndDateFormula(sFilter.formula);
                        }
                    }
                });
            }
        }
    }, [filter]);


    function dateToText(date: string | null, defaultText: string): string {
        if (!date) {
            return defaultText;
        }
        return displayDate(date, field!.format, AggregationType.NO_AGGR, t);
    }

    const startDateToText = (): string => {
        return dateToText(startDate, "-\u221E");
    }

    const endDateToText = (): string => {
        return dateToText(endDate, "+\u221E");
    }

    function isTextValid(value: string | null) {
        return true;
    }


    const renderStartDateEditingContent = (
        onBlur: () => void,
        onEnterKeyPress: (event: React.KeyboardEvent) => void,
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => {
        return (<div className={TW_DateFilterValueEditor_Wrapper}>
            <input
                className={TW_DateFilterValueEditor_editing}
                type="date"
                lang={lang}
                value={(startDate) ? startDate : ""}
                onBlur={onBlur}
                onKeyDown={onEnterKeyPress}
                onChange={onTextChange}
            />
        </div>)
    }

    const renderEndDateEditingContent = (
        onBlur: () => void,
        onEnterKeyPress: (event: React.KeyboardEvent) => void,
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => {
        return (<div className={TW_DateFilterValueEditor_Wrapper}>
            <input
                className={TW_DateFilterValueEditor_editing}
                type="date"
                lang={lang}
                value={(endDate) ? endDate : ""}
                onBlur={onBlur}
                onKeyDown={onEnterKeyPress}
                onChange={onTextChange}
            />
        </div>)
    }


    function validateDate(textValue: string | null | undefined, isStartDate: boolean) {

        if (!selectedPathKey) {
            return;
        }

        const newFieldFilters: Filter[] = [];
        if (isStartDate) {
            if (textValue && textValue.length > 0) {
                newFieldFilters.push({
                    singleFilter: {
                        pathKey: selectedPathKey,
                        operator: SUP_EQUAL,
                        value: textValue,
                        valueKey: null,
                        formula: null
                    },
                    groupFilter: null
                });
            }
        } else {
            if (startDate && startDate.length > 0) {
                newFieldFilters.push({
                    singleFilter: {
                        pathKey: selectedPathKey,
                        operator: SUP_EQUAL,
                        value: startDate,
                        valueKey: null,
                        formula: startDateFormula
                    },
                    groupFilter: null
                });
            }
        }

        if (!isStartDate) {
            if (textValue && textValue.length > 0) {
                newFieldFilters.push({
                    singleFilter: {
                        pathKey: selectedPathKey,
                        operator: INF_EQUAL,
                        value: textValue,
                        valueKey: null,
                        formula: null
                    },
                    groupFilter: null
                });
            }
        } else {
            if (endDate && endDate.length > 0) {
                newFieldFilters.push({
                    singleFilter: {
                        pathKey: selectedPathKey,
                        operator: INF_EQUAL,
                        value: endDate,
                        valueKey: null,
                        formula: endDateFormula
                    },
                    groupFilter: null
                });
            }
        }

        let fieldFilter: Filter | null = {
            singleFilter: null,
            groupFilter: {
                filters: newFieldFilters,
                combinator: AND
            }
        }

        validateFilter(fieldFilter);
    }

    function getLimitDatesFilter(startDate: string | null, startDateFormula: string | null, endDate: string | null, endDateFormula: string | null): Filter | null {

        if (!selectedPathKey) {
            return null;
        }


        const newFieldFilters: Filter[] = [];
        if (startDate && startDate.length > 0) {
            newFieldFilters.push({
                singleFilter: {
                    pathKey: selectedPathKey,
                    operator: SUP_EQUAL,
                    value: startDate,
                    valueKey: null,
                    formula: null
                },
                groupFilter: null
            });
        } else if (startDateFormula && startDateFormula.length > 0) {
            newFieldFilters.push({
                singleFilter: {
                    pathKey: selectedPathKey,
                    operator: SUP_EQUAL,
                    value: null,
                    valueKey: null,
                    formula: startDateFormula
                },
                groupFilter: null
            });
        }

        if (endDate && endDate.length > 0) {
            newFieldFilters.push({
                singleFilter: {
                    pathKey: selectedPathKey,
                    operator: INF_EQUAL,
                    value: endDate,
                    valueKey: null,
                    formula: null
                },
                groupFilter: null
            });
        } else if (endDateFormula && endDateFormula.length > 0) {
            newFieldFilters.push({
                singleFilter: {
                    pathKey: selectedPathKey,
                    operator: INF_EQUAL,
                    value: null,
                    valueKey: null,
                    formula: endDateFormula
                },
                groupFilter: null
            });
        }

        let fieldFilter: Filter | null = {
            singleFilter: null,
            groupFilter: {
                filters: newFieldFilters,
                combinator: AND
            }
        }

        return fieldFilter;
    }

    function validatePeriodFormula(formula: string | null | undefined) {

        if (!selectedPathKey) {
            return;
        }

        const newFieldFilters: Filter[] = [];
        if (formula && formula.length > 0) {
            newFieldFilters.push({
                singleFilter: {
                    pathKey: selectedPathKey,
                    operator: IN,
                    value: null,
                    valueKey: null,
                    formula: formula
                },
                groupFilter: null
            });
        }

        let fieldFilter: Filter | null = {
            singleFilter: null,
            groupFilter: {
                filters: newFieldFilters,
                combinator: AND
            }
        }

        validateFilter(fieldFilter);
    }

    function cancel() {
        setEditStartDate(false);
        setEditEndDate(false);
    }

    function onEnterKeyPressEditing(event: React.KeyboardEvent, isStartDate: boolean) {
        if (AUTHORIZED_KEYS.includes(event.key)) {
            return;
        }
        let newValue;
        if (isStartDate) {
            newValue = startDate;
        } else {
            newValue = endDate;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            validateDate(newValue, isStartDate);
            cancel();
        } else if (event.key === "Tab") {
            event.preventDefault();
            event.stopPropagation();
            validateDate(newValue, isStartDate);
            if (isStartDate) {
                setEditStartDate(false);
                setEditEndDate(true);
            } else {
                setEditEndDate(false);
                setEditStartDate(true);
            }
        } else if (event.key === "Escape") {
            event.preventDefault();
            cancel();
        } else {
            newValue = newValue ? newValue + event.key : event.key;
            if (!isTextValid(newValue)) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    function onEnterKeyPressEditingStartDate(event: React.KeyboardEvent) {
        onEnterKeyPressEditing(event, true);
    }

    function onEnterKeyPressEditingEndDate(event: React.KeyboardEvent) {
        onEnterKeyPressEditing(event, false);
    }

    function onBlurStartDate() {
        validateDate(startDate, true);
    }

    function onBlurEndDate() {
        validateDate(endDate, false);
    }

    function onTextChange(event: React.ChangeEvent<HTMLInputElement>, isStartDate: boolean) {
        let value = event.target.value;
        if (isTextValid(value)) {
            if (isStartDate) {
                setStartDate(value);
            } else {
                setEndDate(value);
            }
        } else {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    function onTextChangeStartDate(event: React.ChangeEvent<HTMLInputElement>) {
        onTextChange(event, true);
    }

    function onTextChangeEndDate(event: React.ChangeEvent<HTMLInputElement>) {
        onTextChange(event, false);
    }

    const onStartClick = () => {
        if (!disabled) {
            setEditStartDate(true);
            setEditEndDate(false);
        }
    }

    const onEndClick = () => {
        if (!disabled) {
            setEditEndDate(true);
            setEditStartDate(false);
        }
    }
    const onAdvancedModeClicked = () => {
        if (!disabled) {
            setIsAdvancedOpen(true);
        }
    }

    const onPeriodFormulaChange = (formula: string) => {
        validatePeriodFormula(formula);
        setIsAdvancedOpen(false);
    }

    const onLimitDatesChange = (startDate: string | null, startDateFormula: string | null, endDate: string | null, endDateFormula: string | null) => {
        const fieldFilter = getLimitDatesFilter(startDate, startDateFormula, endDate, endDateFormula);
        if (fieldFilter) {
            validateFilter(fieldFilter);
            setStartDate(startDate);
            setStartDateFormula(startDateFormula);
            setEndDate(endDate);
            setEndDateFormula(endDateFormula);
            setPeriodFormula(null);
        }
        setIsAdvancedOpen(false);
    }

    const onDeleteClicked = () => {
        if (!selectedPathKey) return;

        const newFieldFilters: Filter[] = [];
        newFieldFilters.push({
            singleFilter: {
                pathKey: selectedPathKey,
                operator: SUP_EQUAL,
                value: startDate,
                valueKey: null,
                formula: null
            },
            groupFilter: null
        });

        newFieldFilters.push({
            singleFilter: {
                pathKey: selectedPathKey,
                operator: INF_EQUAL,
                value: endDate,
                valueKey: null,
                formula: null
            },
            groupFilter: null
        });

        let fieldFilter: Filter | null = {
            singleFilter: null,
            groupFilter: {
                filters: newFieldFilters,
                combinator: AND
            }
        }
        validateFilter(fieldFilter);
        setPeriodFormula(null);
        setStartDateFormula(null);
        setEndDateFormula(null);
        setIsAdvancedOpen(false);
    }

    const openAdvancedMode = () => {
        if (!disabled) {
            setIsAdvancedOpen(true);
        }
    }

    if (periodFormula) {
        let shortcutLabel = shortcuts.find(shortcut => shortcut.formula === periodFormula)?.label;
        let formulaLabel = shortcutLabel ? shortcutLabel : periodFormula;

        return (
            <div className={TW_FilterEditorValue_Wrapper}>

                <div className="flex flex-row justify-center items-center min-w-24 w-24">
                    <span className="text-sm text-gray-600 text-nowrap">{t("filter.period.in.label")}</span>
                </div>
                <div className="flex flex-row justify-center items-center gap-2 grow overflow-hidden">
                    <Button className={TW_DateFilterValueEditor_PeriodFormulaButton} onClick={onAdvancedModeClicked}>
                        {formulaLabel}
                    </Button>
                </div>
                <Popover open={isAdvancedOpen} onOpenChange={openAdvancedMode}>
                    <PopoverTrigger onClick={openAdvancedMode}>
                        <FunctionSVG className={TW_DateFilterValueEditor_FunctionButton + (disabled ? " cursor-not-allowed opacity-60" : " cursor-pointer")} />
                    </PopoverTrigger>
                    <PopoverContent className="w-96 p-1">
                        <AdvancedPeriodEditor
                            fieldKey={fieldKey}
                            periodFormula={periodFormula}
                            onPeriodFormulaChange={onPeriodFormulaChange}
                            startDateFormula={startDateFormula}
                            onLimitDatesChange={onLimitDatesChange}
                            endDateFormula={endDateFormula}
                            onDeleteClicked={onDeleteClicked}
                            dateFormat={field!.format}
                            startDate={startDate}
                            endDate={endDate} />
                    </PopoverContent>
                </Popover>
            </div>
        )
    } else {
        return (
            <div className={TW_FilterEditorValue_Wrapper}>
                <span className={TW_PeriodFilterValueEditor_From}>{t("filter.period.from.label")}</span>
                {editStartDate && !disabled && renderStartDateEditingContent(onBlurStartDate, onEnterKeyPressEditingStartDate, onTextChangeStartDate)}
                {!editStartDate && !startDateFormula && <div className={TW_DateFilterValueEditor_Wrapper} onClick={onStartClick}>
                    <div className={TW_DateFilterValueEditor + (disabled ? " cursor-not-allowed opacity-60" : " cursor-pointer")}>
                        {startDateToText()}
                    </div>
                </div>}
                {!editStartDate && startDateFormula &&
                    <Button className={TW_DateFilterValueEditor_DateFormulaButton + (disabled ? " cursor-not-allowed opacity-60" : " cursor-pointer")} onClick={openAdvancedMode}>
                        {startDateFormula}
                    </Button>
                }
                <span className={TW_PeriodFilterValueEditor_To}>{t("filter.period.to.label")}</span>
                {editEndDate && !disabled && renderEndDateEditingContent(onBlurEndDate, onEnterKeyPressEditingEndDate, onTextChangeEndDate)}
                {!editEndDate && !endDateFormula && <div className={TW_DateFilterValueEditor_Wrapper} onClick={onEndClick}>
                    <div className={TW_DateFilterValueEditor + (disabled ? " cursor-not-allowed opacity-60" : " cursor-pointer")}>
                        {endDateToText()}
                    </div>
                </div>}
                {!editEndDate && endDateFormula &&
                    <Button className={TW_DateFilterValueEditor_DateFormulaButton + (disabled ? " cursor-not-allowed opacity-60" : " cursor-pointer")} onClick={openAdvancedMode}>
                        {endDateFormula}
                    </Button>
                }
                <Popover open={isAdvancedOpen} onOpenChange={openAdvancedMode}>
                    <PopoverTrigger onClick={openAdvancedMode}>
                        <FunctionSVG className={TW_DateFilterValueEditor_FunctionButton + (disabled ? " cursor-not-allowed opacity-60" : " cursor-pointer")} />
                    </PopoverTrigger>
                    <PopoverContent className="w-96 p-1">
                        <AdvancedPeriodEditor
                            fieldKey={fieldKey}
                            periodFormula={periodFormula}
                            onPeriodFormulaChange={onPeriodFormulaChange}
                            startDateFormula={startDateFormula}
                            onLimitDatesChange={onLimitDatesChange}
                            endDateFormula={endDateFormula}
                            onDeleteClicked={onDeleteClicked}
                            dateFormat={field!.format}
                            startDate={startDate}
                            endDate={endDate} />
                    </PopoverContent>
                </Popover>
            </div>
        );
    }

}

type AdvancedPeriodEditorProps = {
    fieldKey: FieldKey | undefined;
    startDate: string | null;
    endDate: string | null;
    periodFormula: string | null;
    startDateFormula: string | null;
    endDateFormula: string | null;
    onPeriodFormulaChange: (formula: string) => void;
    onLimitDatesChange: (startDate: string | null, startDateFormula: string | null, endDate: string | null, endDateFormula: string | null) => void;
    onDeleteClicked: () => void;
    dateFormat: Map<string, any> | undefined | null;
}

function AdvancedPeriodEditor({ fieldKey, startDate, endDate, periodFormula, onPeriodFormulaChange, startDateFormula, endDateFormula, onLimitDatesChange, onDeleteClicked, dateFormat }: AdvancedPeriodEditorProps) {
    const { t } = useTranslation();
    const shortcuts = getShortcuts(t);
    const [shortcutsOpen, setShortcutsOpen] = useState(true);
    const [periodFormulaOpen, setPeriodFormulaOpen] = useState(false);
    const [periodFormulaValue, setPeriodFormulaValue] = useState<string | null>(periodFormula);
    const [limitDatesFormulaOpen, setLimitDatesFormulaOpen] = useState(false);
    const [startDateFormulaOpen, setStartDateFormulaOpen] = useState(false);
    const [endDateFormulaOpen, setEndDateFormulaOpen] = useState(false);
    const [startDateFormulaValue, setStartDateFormulaValue] = useState<string | null>(startDateFormula);
    const [endDateFormulaValue, setEndDateFormulaValue] = useState<string | null>(endDateFormula);
    const [editStartDate, setEditStartDate] = useState(false);
    const [editEndDate, setEditEndDate] = useState(false);
    const [startDateValue, setStartDateValue] = useState<string | null>(startDate);
    const [endDateValue, setEndDateValue] = useState<string | null>(endDate);
    const wsColor = useModelStore((state) => state.wsColor);
    const selectedWorkspace = useModelStore((state) => state.selectedWorkspace);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const trashHover = useIconHover("#00000088", wsColor);
    const lang = useUserStore((state) => state.lang);

    useEffect(() => {
        if (periodFormula) {
            if (!shortcuts.find(shortcut => shortcut.formula === periodFormula)) {
                openPeriodFormula();
            } else {
                openShortcuts();
            }
        } else if (startDateFormula !== null || endDateFormula !== null) {
            openLimitDatesFormula();
        } else {
            openShortcuts();
        }
    }, []);

    function shortcutButton(label: string, shortcutFormula: string) {

        const buttonStyle = {
            borderColor: (shortcutFormula === periodFormulaValue) ? wsColor : GRAY_300_COLOR,
        }
        return (
            <Button key={label} className={TW_AdvancedPeriodEditorShortcutButton} onClick={() => onPeriodFormulaChange(shortcutFormula)} style={buttonStyle}>
                {label}
            </Button>
        )
    }

    const openShortcuts = () => {
        setShortcutsOpen(true);
        setPeriodFormulaOpen(false);
        setLimitDatesFormulaOpen(false);
    }

    const openPeriodFormula = () => {
        setPeriodFormulaOpen(true);
        setShortcutsOpen(false);
        setLimitDatesFormulaOpen(false);
    }

    const openLimitDatesFormula = () => {
        setLimitDatesFormulaOpen(true);
        setPeriodFormulaOpen(false);
        setShortcutsOpen(false);
    }

    const onPeriodFormulaValidate = () => {
        checkFormula(selectedWorkspace?._key, fieldKey?.objectKey, undefined, periodFormulaValue || "", () => {
            onPeriodFormulaChange(periodFormulaValue || "");
        }, (errorI18n, _, errorParams) => {
            setErrorMessage(translate(t, errorI18n, errorParams));
        });
    }

    function dateToText(date: string | null, defaultText: string): string {
        if (!date) {
            return defaultText;
        }
        return displayDate(date, dateFormat, AggregationType.NO_AGGR, t);
    }

    const startDateToText = (): string => {
        return dateToText(startDateValue, "-\u221E");
    }

    const endDateToText = (): string => {
        return dateToText(endDateValue, "+\u221E");
    }

    function isTextValid(value: string | null) {
        return true;
    }


    const renderStartDateEditingContent = (
        onBlur: () => void,
        onEnterKeyPress: (event: React.KeyboardEvent) => void,
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => {
        return (<div className={TW_DateFilterValueEditor_Wrapper}>
            <input
                className={TW_DateFilterValueEditor_editing}
                type="date"
                lang={lang}
                value={(startDateValue) ? startDateValue : ""}
                onBlur={onBlur}
                onKeyDown={onEnterKeyPress}
                onChange={onTextChange}
            />
        </div>)
    }

    const renderEndDateEditingContent = (
        onBlur: () => void,
        onEnterKeyPress: (event: React.KeyboardEvent) => void,
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => {
        return (<div className={TW_DateFilterValueEditor_Wrapper}>
            <input
                className={TW_DateFilterValueEditor_editing}
                type="date"
                lang={lang}
                value={(endDateValue) ? endDateValue : ""}
                onBlur={onBlur}
                onKeyDown={onEnterKeyPress}
                onChange={onTextChange}
            />
        </div>)
    }


    function onEnterKeyPressEditing(event: React.KeyboardEvent, isStartDate: boolean) {
        if (AUTHORIZED_KEYS.includes(event.key)) {
            return;
        }
        let newValue;
        if (isStartDate) {
            newValue = startDateValue;
        } else {
            newValue = endDateValue;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            validateDate(newValue, isStartDate);
            if (isStartDate) {
                setEditStartDate(false);
            } else {
                setEditEndDate(false);
            }
        } else if (event.key === "Tab") {
            event.preventDefault();
            event.stopPropagation();
            validateDate(newValue, isStartDate);
            if (isStartDate) {
                setEditStartDate(false);
                setEditEndDate(true);
            } else {
                setEditEndDate(false);
                setEditStartDate(true);
            }
        } else if (event.key === "Escape") {
            event.preventDefault();
            if (isStartDate) {
                setEditStartDate(false);
            } else {
                setEditEndDate(false);
            }
        } else {
            newValue = newValue ? newValue + event.key : event.key;
            if (!isTextValid(newValue)) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    function onEnterKeyPressEditingStartDate(event: React.KeyboardEvent) {
        onEnterKeyPressEditing(event, true);
    }

    function onEnterKeyPressEditingEndDate(event: React.KeyboardEvent) {
        onEnterKeyPressEditing(event, false);
    }

    function onBlurStartDate() {
        validateDate(startDateValue, true);
        setEditStartDate(false);
    }

    function onBlurEndDate() {
        validateDate(endDateValue, false);
        setEditEndDate(false);
    }

    function onTextChangeStartDate(event: React.ChangeEvent<HTMLInputElement>) {
        onTextChange(event, true);
    }

    function onTextChangeEndDate(event: React.ChangeEvent<HTMLInputElement>) {
        onTextChange(event, false);
    }

    function onStartClick() {
        setEditStartDate(true);
    }

    function onEndClick() {
        setEditEndDate(true);
    }

    function onTextChange(event: React.ChangeEvent<HTMLInputElement>, isStartDate: boolean) {
        let value = event.target.value;
        if (isTextValid(value)) {
            if (isStartDate) {
                setStartDateValue(value);
            } else {
                setEndDateValue(value);
            }
        }
    }

    function validateDate(date: string | null, isStartDate: boolean) {
        if (isStartDate) {
            setStartDateValue(date);
        } else {
            setEndDateValue(date);
        }
    }

    function onLimitDatesFormulaValidate() {
        checkFormula(selectedWorkspace?._key, fieldKey?.objectKey, undefined, startDateFormulaValue || "", () => {
            checkFormula(selectedWorkspace?._key, fieldKey?.objectKey, undefined, endDateFormulaValue || "", () => {
                onLimitDatesChange(startDateValue, startDateFormulaValue, endDateValue, endDateFormulaValue);
            }, (errorI18n, _, errorParams) => {
                setErrorMessage(translate(t, errorI18n, errorParams));
            });
        }, (errorI18n, _, errorParams) => {
            setErrorMessage(translate(t, errorI18n, errorParams));
        });
    }

    function onEndDateFormulaOpenClicked() {
        setEndDateFormulaOpen(!endDateFormulaOpen);
    }

    function onStartDateFormulaOpenClicked() {
        setStartDateFormulaOpen(!startDateFormulaOpen);
    }

    function onStartDateFormulaDelete() {
        setStartDateFormulaValue(null);
    }

    function onEndDateFormulaDelete() {
        setEndDateFormulaValue(null);
    }

    function onStartDateFomulaChanged(formula: string | null) {
        setStartDateFormulaValue(formula);
        if (formula) {
            setStartDateValue(null);
        }
    }

    function onEndDateFomulaChanged(formula: string | null) {
        setEndDateFormulaValue(formula);
        if (formula) {
            setEndDateValue(null);
        }
    }

    return (
        <div className={TW_AdvancedPeriodEditorContainer}>
            <div className="flex flex-row justify-end items-center w-full relative">
                <TooltipProvider>
                    <Tooltip delayDuration={100}>
                        <TooltipTrigger asChild>
                            <FunctionStruckSVG className={TW_AdvancedPeriodEditorTrashIcon} onClick={onDeleteClicked} {...trashHover} />
                        </TooltipTrigger>
                        <TooltipContent>{t("filter.period.formula.delete.tooltip")}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            <div className={TW_AdvancedPeriodEditorSectionContainer}>
                <div className={TW_AdvancedPeriodEditorSectionHeader} onClick={openShortcuts}>
                    <Label className={TW_AdvancedPeriodEditorSectionLabel}>{t("filter.period.shortcuts.label")}</Label>
                    {shortcutsOpen && <ChevronDown className={TW_AdvancedPeriodEditorSectionIcon} />}
                    {!shortcutsOpen && <ChevronRight className={TW_AdvancedPeriodEditorSectionIcon} />}
                </div>
                <div className={TW_AdvancedPeriodEditorShortcutButtonsContainer + (shortcutsOpen ? " block" : " hidden")}>
                    {shortcuts.map((shortcut) => shortcutButton(shortcut.label, shortcut.formula))}
                </div>
            </div>
            <div className={TW_AdvancedPeriodEditorSectionContainer}>
                <div className={TW_AdvancedPeriodEditorSectionHeader} onClick={openPeriodFormula}>
                    <Label className={TW_AdvancedPeriodEditorSectionLabel}>{t("filter.period.formula.label")}</Label>
                    {periodFormulaOpen && <ChevronDown className={TW_AdvancedPeriodEditorSectionIcon} />}
                    {!periodFormulaOpen && <ChevronRight className={TW_AdvancedPeriodEditorSectionIcon} />}
                </div>
                <div className={TW_AdvancedPeriodEditorPeriodFormulaContainer + (periodFormulaOpen ? " block" : " hidden")}>
                    <FormulaEditor formula={periodFormulaValue || undefined} objectKey={fieldKey?.objectKey} onFormulaChange={setPeriodFormulaValue} fieldSelectorEnabled={false} />
                    <Button className={TW_AdvancedPeriodEditorPeriodFormulaButtonValidate} onClick={onPeriodFormulaValidate}>{t("filter.period.formula.validate.button.label")}</Button>
                    {errorMessage && <span className="text-red-500 text-sm">{errorMessage}</span>}
                </div>
            </div>
            <div className={TW_AdvancedPeriodEditorSectionContainer}>
                <div className={TW_AdvancedPeriodEditorSectionHeader} onClick={openLimitDatesFormula}>
                    <Label className={TW_AdvancedPeriodEditorSectionLabel}>{t("filter.period.limit.dates.formula.label")}</Label>
                    {limitDatesFormulaOpen && <ChevronDown className={TW_AdvancedPeriodEditorSectionIcon} />}
                    {!limitDatesFormulaOpen && <ChevronRight className={TW_AdvancedPeriodEditorSectionIcon} />}
                </div>
                <div className={TW_AdvancedPeriodEditorPeriodFormulaContainer + (limitDatesFormulaOpen ? " block" : " hidden")}>
                    <div className={TW_AdvancedPeriodEditorDateFormulasContainer}>
                        <div className={TW_AdvancedPeriodEditorDateFormulaItem + " relative"}>
                            <span className={TW_PeriodFilterValueEditor_From + " w-8"}>{t("filter.period.from.label")}</span>
                            {editStartDate && renderStartDateEditingContent(onBlurStartDate, onEnterKeyPressEditingStartDate, onTextChangeStartDate)}
                            {(!editStartDate && !startDateFormulaValue) && <div className={TW_DateFilterValueEditor_Wrapper} onClick={onStartClick}>
                                <div className={TW_DateFilterValueEditor + " cursor-pointer"}>
                                    {startDateToText()}
                                </div>
                            </div>}
                            {!editStartDate && startDateFormulaValue &&
                                <Button className={TW_DateFilterValueEditor_DateFormulaButton} onClick={onStartDateFormulaOpenClicked}>
                                    {startDateFormulaValue}
                                </Button>
                            }
                            <FunctionSVG color={(startDateFormulaOpen ? GRAY_800_COLOR : GRAY_600_COLOR)} className={(startDateFormulaOpen ? TW_AdvancedPeriodEditorFormulaTogglePressed : TW_AdvancedPeriodEditorFormulaToggle)} onClick={onStartDateFormulaOpenClicked} />
                            <FunctionStruckSVG color={GRAY_600_COLOR} className="absolute h-4 w-4 right-0 cursor-pointer" onClick={onStartDateFormulaDelete} />
                        </div>
                        {startDateFormulaOpen && <FormulaEditor formula={startDateFormulaValue || undefined} objectKey={fieldKey?.objectKey} onFormulaChange={onStartDateFomulaChanged} fieldSelectorEnabled={false} />}
                        <div className={TW_AdvancedPeriodEditorDateFormulaItem + " relative"}>
                            <span className={TW_PeriodFilterValueEditor_To + " w-8"}>{t("filter.period.to.label")}</span>
                            {editEndDate && renderEndDateEditingContent(onBlurEndDate, onEnterKeyPressEditingEndDate, onTextChangeEndDate)}
                            {(!editEndDate && !endDateFormulaValue) &&
                                <div className={TW_DateFilterValueEditor_Wrapper} onClick={onEndClick}>
                                    <div className={TW_DateFilterValueEditor + " cursor-pointer"}>
                                        {endDateToText()}
                                    </div>
                                </div>}
                            {!editEndDate && endDateFormulaValue &&
                                <Button className={TW_DateFilterValueEditor_DateFormulaButton} onClick={onEndDateFormulaOpenClicked}>
                                    {endDateFormulaValue}
                                </Button>}
                            <FunctionSVG color={(endDateFormulaOpen ? GRAY_800_COLOR : GRAY_600_COLOR)} className={(endDateFormulaOpen ? TW_AdvancedPeriodEditorFormulaTogglePressed : TW_AdvancedPeriodEditorFormulaToggle)} onClick={onEndDateFormulaOpenClicked} />
                            <FunctionStruckSVG color={GRAY_600_COLOR} className="absolute h-4 w-4 right-0 cursor-pointer" onClick={onEndDateFormulaDelete} />
                        </div>
                        {endDateFormulaOpen && <FormulaEditor formula={endDateFormulaValue || undefined} objectKey={fieldKey?.objectKey} onFormulaChange={onEndDateFomulaChanged} fieldSelectorEnabled={false} />}
                        <div className={TW_AdvancedPeriodEditorPeriodFormulaContainer}>
                            <Button className={TW_AdvancedPeriodEditorPeriodFormulaButtonValidate} onClick={onLimitDatesFormulaValidate}>{t("filter.period.formula.validate.button.label")}</Button>
                            {errorMessage && <span className="text-red-500 text-sm">{errorMessage}</span>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

